import Interieur from "../arrangement/Interieur";
import PriorityIntervals from "./PriorityIntervals";
import Fit from "./Fit";
import Measurer from "./Measurer";
import Maths from "oc-commons/Maths";

const epsilon = 0.01;

export default class Space {

    // Startposition (von space.bottom)

    offset = 0;

    // alle folgenden Angaben sind Sizes

    inner = 0;

    space = {
        bottom: 0,
        top: 0,
    };

    collapse = {
        bottom: false,
        top: false,
    };

    touches = {
        bottom: false,
        top: false,
    };

    eq(a, b) {
        return Math.abs(a - b) < epsilon;
    }

    _touches(element, key, interval) {
        const index = key=='bottom' ? 0 : 1;
        const minMax = key=='bottom' ? 'min' : 'max';
        if (interval==null) return false;
        return this.eq(interval[index], element.box[minMax].y);
    }

    spaceAndType(element, key, intervalCallback) {
        const spaceInterval = intervalCallback(i => i[2] === Measurer.SPACE || i[2] === Measurer.COLLAPSE);
        const space = PriorityIntervals.intervalSize(spaceInterval);
        const collapseInterval = intervalCallback(i => i[2] === Measurer.COLLAPSE);
        const collapse = PriorityIntervals.intervalSize(collapseInterval);

        this.touches[key] = this._touches(element, key, collapseInterval) || this._touches(element, key, spaceInterval);
        if (space < epsilon) {
            this.space[key] = 0;
            this.collapse[key] = false;
            return;
        }
        if (space < collapse + epsilon) {
            this.space[key] = collapse;
            this.collapse[key] = true;
            return;
        }
        this.space[key] = space;
        this.collapse[key] = false;
    }

    setFromFree(element, intervals, index, ignoreSpace = false) {
        this.inner = PriorityIntervals.intervalSize(intervals.list[index]);
        if (!ignoreSpace) {
            this.spaceAndType(element, 'bottom', c => intervals.backwardInterval(index - 1, c));
            this.spaceAndType(element, 'top', c => intervals.forwardInterval(index + 1, c));
        }
        this.offset = intervals.list[index][0] - this.space.bottom;
        return this;
    }


    /**
     * versucht, das übergebene Interior einzupassen, und gibt alle möglichen y-Positionen des Interieurs (Unterkante)
     * als Intervalle bezogen auf den eigenen Offset zurück.
     */
    getFit(interieur: Interieur, occupiedAsZero: boolean, centerMode:boolean): ?Fit {
        // Das folgende ist echt komplexe Raketenscheisse.. also irgendwie falsch.
        // Nochmal ein paar Schritte zurück. Was wollen wir eigentlich?

        // Wir haben ein Interieur mit:
        // - Freiraum (space) oben und unten, der frei bleiben muss, aber anderen Freiraum überschneiden darf
        // - einer Höhe, die abhängig davon ist, ob das Interieur oben oder unten anliegt (am Boden)
        // - needsBoden oben und unten, gibt an, ob ein Konstruktionsboden vorhanden sein muss.
        //   ein Konstruktionsboden bedeutet:
        //   - entweder wird ein Boden erzeugt (neuer Occupied-Bereich!)
        //     und es wird die Fach-Mindesthöhe als Freiraum gesetzt
        //   - oder es wird an einem vorhandenen Boden angedockt, wodurch die Fachhöhe (this.space) zu null wird
        //   - ein Konstruktionsboden und Interieur-Freiraum schließt sich aus

        // Wir haben einen Space (this), welcher aus dem freien Intervall und den angrenzenden Space/Occupied-Intervallen
        // des Elementes gebildet wird, mit:
        // - inner: der freie Bereich, der beliebig belegt werden darf
        // - space: der Freiraum oben und unten, der frei bleiben muss, aber anderen Freiraum überschneiden darf
        // - collapse: ob der Freiraum ein Fach-Freiraum ist und im Falle eines Konstruktionsbodens zu 0 werden darf.
        // - touches: ob der Space ganz oben oder unten am Element anliegt

        // Wir haben die Parameter:
        // - occupiedAsZero, ob die Größe des Interieurs variable ist und für diese Abfrage erst mal als 0 angenommen wird
        // - centerMode: ob das zurückgegebene Fit bezogen auf den Mittelpunkt des Interieurs erzeugt wird

        // Wir haben die Ausgabe, ein Fit:
        // - minCollapse ist die minimale Y-Position, wenn die Fachhöhe oben kollabiert
        // - min-max ist das Intervall der Y-Positionen, in dem das Objekt frei verschoben werden kann
        // - maxCollapse ist die maximale Y-Position, wenn die Fachhöhe unten kollabiert

        const fach_hoehe = interieur.schrank.options.getFloat('fach_hoehe');
        const boden_hoehe = interieur.schrank.options.getFloat('brett_staerke');
        
        const interieurHeight = touches => occupiedAsZero ? 0 : interieur.getHeight(touches.bottom,touches.top);

        const maxSpace = {bottom:0,top:0};
        const isCollapsable = {bottom:false,top:false};
        const interieurSpace = {bottom:0,top:0};
        const deltaSpace = {bottom:0,top:0};

        ['bottom','top'].forEach(side=>{
            maxSpace[side]=Math.max(this.space[side], interieur.space[side]);
            isCollapsable[side]=this.collapse[side] && interieur.needsBoden[side] && this.eq(this.space[side], fach_hoehe + boden_hoehe);
            interieurSpace[side] = interieur.needsBoden[side] ? fach_hoehe + boden_hoehe : interieur.space[side];
            deltaSpace[side] = interieurSpace[side]>this.space[side] ? interieurSpace[side]-this.space[side] : 0;
        });

        // Wir gehen bei der folgenden Betrachtung von den sehr einschränkenden Grenzfällen aus und
        // nähern uns dem Fall an, wo das Interieur freier positionierbar ist.

        const collapseCalculator = (collapsed) => {
            let free = this.inner;
            const touches = {bottom:false,top:false};
            let offset = 0;
            ['bottom','top'].forEach(side=>{
                if (collapsed[side] && isCollapsable[side]) {
                    free += this.space[side];
                    touches[side] = this.touches[side];
                } else {
                    free -= deltaSpace[side];
                    touches[side] = this.touches[side] && maxSpace[side]<epsilon;
                    if (side==='bottom') {
                        offset += maxSpace[side];
                    }
                }
            });
            const occupied = interieurHeight(touches);
            const left = free-occupied;
            return {
                free,
                touches,
                offset,
                left,
                occupied,
                fits: left+epsilon>0,
                fitsExact: Math.abs(left)<epsilon
            };
        };

        const translateFit = (fit,occupied) => {
            fit.translate(this.offset);
            if (centerMode) {
                fit.translate(occupied/2);
            }
        };

        // Zunächst schauen wir, ob das Interieur überhaupt reinpasst. Dazu werden alle kollabierenden Bereiche
        // als kollabiert angenommen. Nicht-kollabierender Freiraum muss erhalten bleiben.

        const maxCollapse = collapseCalculator({bottom:true,top:true});

        // 1 Fall: Wenn das Interieur trotz maximaler Kollabierung nicht passt, kein Fit
        if (!maxCollapse.fits) {
            return null;
        }

        // 2. Fall: Wenn das Interieur exakt reinpasst, wird der eine mögliche Einfügepunkt als Fit zurückgegeben
        if (maxCollapse.fitsExact) {
            const f = new Fit();
            f.maxCollapse = f.max = f.minCollapse = f.min = maxCollapse.offset;
            translateFit(f,maxCollapse.occupied);
            return f;
        }

        // Wir wissen jetzt, dass das Interieur reinpasst, aber eventuell ist noch etwas Luft übrig.
        // Es könnte aber trotzdem nur einen Einfügepunkt geben, weil z.B.
        // nur top oder bottom collapsable ist. Es könnten aber auch zwei Einfügepunkte bei top und bottom sein,
        // und eventuell sogar ein bewegliches Intervall in der Mitte.
        // Das folgende ist der ältere Code, der vorher alle Fälle abgeprüft hat, aber in den Grenzfällen 1. und 2.
        // nicht immer funktioniert hat (Krawattenauszug bei nicht passendem Element)

        const innerLeft = this.inner + Math.min(0, this.space.top - maxSpace.top) + Math.min(0, this.space.bottom - maxSpace.bottom);
        // Höhe des Interiers für den Fall: Nicht touched bottom oder top
        const height = interieurHeight({bottom:false, top:false});
        // Höhe für den Fall touched bottom oder nicht touched bottom
        const heightBottom = interieurHeight({bottom:true, top:false});
        // Höhe für den Fall touched top oder nicht touched top
        const heightTop = interieurHeight({bottom:false, top:true});
        const moveRange = innerLeft - (occupiedAsZero ? 0 : height);

        const f = new Fit();
        f.minCollapse = f.min = maxSpace.bottom;
        f.maxCollapse = f.max = f.min + moveRange;

        /**
         * Hier wird das minCollapse bzw. maxCollapse gegebenenfalls noch ein Stück verschoben
         * 
         * Veränderung nur bei isCollapsable, da ansonsten der maxSpace.bottom eingehalten werden muss
         * 
         * Falls das Interieur touches.bottom oder touches.top muss eine Korrektur Werte minCollapse und maxCollapse
         * um die Höhe der Überstände / 2 nach unten bzw. oben vorgenommen werden --> (height-heightBottom) / 2
         * Das ist der Unterschied der sich im Gegensatz zu einem Interieur an anderer Position ergibt
         * 
         * Wenn es sich nicht um einen Measurer im centeredMode handelt, sollte diese Verschiebung, sowie die im translateFit
         * außen vor gelassen werden
         */

        if (isCollapsable.bottom) {
            f.minCollapse -= this.space.bottom + ((this.touches.bottom && centerMode) ? (height-heightBottom) / 2 : 0);
        }
        if (isCollapsable.top) {
            f.maxCollapse += this.space.top + ((this.touches.top && centerMode) ? (height-heightTop) / 2 : 0);
        }

        f.min = Maths.cutBetween(f.minCollapse, f.min, f.maxCollapse);
        f.max = Maths.cutBetween(f.minCollapse, f.max, f.maxCollapse);

        /**
         * Hier wird das Fit um das offset und wenn (centerMode) um die Hälfte der Höhe eines NOT-touched Interieurs verschoben
         * Diese wird immer gemacht, die Korrektur bei touched findet oben schon statt (vorheriger Block)
         */

        translateFit(f, height);

        return f;
    }

    /**
     * gibt den gesamten Space als Fit zurück
     */
    getMaxFit() {
        const f = new Fit();
        f.minCollapse = f.min = 0;
        f.maxCollapse = f.max = this.sum;
        f.translate(this.offset);
        return f;
    }

    get sum() {
        return this.space.bottom + this.inner + this.space.top;
    }

}
